import {
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    FormControlLabel,
    Checkbox,
    Typography,
} from "@mui/material";
import React, { useEffect, useState, useRef } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
// import axios from '../../../utils/axiosInstance';
import { enqueueSnackbar } from "notistack";
import UserTable from "./UserTable";
import { useUser } from "../../../context/UserContext";
import { ROLE, usePermissions } from "../../../utils/permissions";
import axios from "../../../utils/axiosInstance";

type CmsModule = {
    moduleName: string;
    moduleLabel: string;
    moduleCategory?: string;
};

const FALLBACK_MODULES: CmsModule[] = [
    { moduleName: "dsar", moduleLabel: "Dsar" },
    {
        moduleName: "mam",
        moduleLabel: "Avails+",
        moduleCategory: "MAM",
    },
    { moduleName: "genre", moduleLabel: "Genre Management" },
];

function ensureFallbackModules(modules: CmsModule[]): CmsModule[] {
    const existing = new Set(
        modules.map((module) => `${module?.moduleName}`.toLowerCase())
    );
    const additions = FALLBACK_MODULES.filter(
        (module) => !existing.has(module.moduleName.toLowerCase())
    );
    return [...modules, ...additions];
}

function groupModulesByCategory(modules: CmsModule[]) {
    const byCategory: Record<string, CmsModule[]> = {};
    for (const module of modules) {
        const category = module.moduleCategory || "General";
        if (!byCategory[category]) byCategory[category] = [];
        byCategory[category].push(module);
    }

    const categories = Object.keys(byCategory).sort((a, b) => {
        if (a === "General") return -1;
        if (b === "General") return 1;
        return a.localeCompare(b);
    });

    return categories.map((category) => ({
        category,
        modules: byCategory[category],
    }));
}

function PermissionModuleGrid({
    modules,
    permissionType,
    values,
    onPermissionChange,
}: {
    modules: CmsModule[];
    permissionType: "viewPermissions" | "editPermissions";
    values: { viewPermissions: string[]; editPermissions: string[] };
    onPermissionChange: (
        permissionType: "viewPermissions" | "editPermissions",
        moduleName: string
    ) => void;
}) {
    return (
        <>
            {groupModulesByCategory(modules).map(({ category, modules: grouped }) => (
                <Grid item xs={12} key={`${permissionType}-${category}`}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                        {category}
                    </Typography>
                    <Grid container spacing={2}>
                        {grouped.map((module) => (
                            <Grid item xs={2} key={module.moduleName}>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={values[permissionType].includes(
                                                module.moduleName
                                            )}
                                            onChange={() =>
                                                onPermissionChange(
                                                    permissionType,
                                                    module.moduleName
                                                )
                                            }
                                            name={`userAttributes.${permissionType}.${module.moduleName}`}
                                            disabled={
                                                permissionType === "viewPermissions" &&
                                                values.editPermissions.includes(
                                                    module.moduleName
                                                )
                                            }
                                        />
                                    }
                                    label={module.moduleLabel}
                                />
                            </Grid>
                        ))}
                    </Grid>
                </Grid>
            ))}
        </>
    );
}

const UserManagement = () => {
    const { hasRole } = usePermissions();
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [saveLoader, setSaveLoader] = useState(false);
    const [modules, setModules] = useState<any[]>([]);
    const [roles, setRoles] = useState<any>([]);
    const userTableRef = useRef<any>(null);
    const [isEdit, setIsEdit] = useState(false);
    const [permissionError, setPermissionError] = useState("");

    useEffect(() => {
        getModules();
    }, []);

    const initialState = {
        username: "",
        userAttributes: {
            firstName: "",
            lastName: "",
            email: "",
            role: "",
            viewPermissions: [],
            editPermissions: [],
        },
    };

    const {
        values,
        touched,
        errors,
        handleBlur,
        handleSubmit,
        handleChange,
        setFieldValue,
        resetForm,
        setValues,
    }: any = useFormik({
        initialValues: initialState,
        validationSchema: Yup.object({
            username: Yup.string().required("Username is required"),
            userAttributes: Yup.object({
                firstName: Yup.string().required("First name is required"),
                lastName: Yup.string().required("Last name is required"),
                email: Yup.string()
                    .email("Invalid email")
                    .required("Email is required"),
                role: Yup.string().required("Role is required"),
            }),
        }),
        onSubmit: (values) => {
            if (
                values.userAttributes.viewPermissions.length === 0 &&
                values.userAttributes.editPermissions.length === 0
            ) {
                setPermissionError(
                    "At least one permission must be selected in either view or edit modules."
                );
                return;
            }
            setPermissionError("");
            const payload = {
                ...values,
                userAttributes: {
                    ...values.userAttributes,
                    viewPermissions:
                        values.userAttributes.viewPermissions.join(","),
                    editPermissions:
                        values.userAttributes.editPermissions.join(","),
                },
            };
            if (isEdit) updateUser(payload);
            else createUser(payload);
        },
    });

    const closeModal = () => {
        setIsEdit(false);
        setFieldValue("", initialState);
        resetForm();
        setShowCreateModal(false);
        setPermissionError("");
    };

    const getModules = async () => {
        try {
            const response = await axios.get(
                `${process.env.BASE_URL}api/v1/cms/user/modules`
            );
            const apiModules = response.data.data?.modules || [];
            const normalized = Array.isArray(apiModules) ? apiModules : [];
            setModules(ensureFallbackModules(normalized));
            setRoles(response.data.data?.roles);
        } catch (error) {
            console.error("Error in getting master information", error);
            enqueueSnackbar("Failed to fetch modules", { variant: "error" });
        }
    };

    const createUser = async (values: any) => {
        try {
            setSaveLoader(true);
            const response = await axios.post(
                `${process.env.BASE_URL}api/v1/cms/user`,
                values
            );
            setSaveLoader(false);
            if (response.data?.success) {
                closeModal();
                enqueueSnackbar("User created successfully.", {
                    variant: "success",
                });
                if (
                    userTableRef.current &&
                    userTableRef.current.refreshUserList
                ) {
                    userTableRef.current.refreshUserList();
                }
                resetForm();
            } else {
                enqueueSnackbar(response.data.message, { variant: "error" });
            }
        } catch (error: any) {
            setSaveLoader(false);
            if (
                error.response?.data?.error?.message?.name ===
                "UsernameExistsException"
            ) {
                enqueueSnackbar("Email already exists", { variant: "warning" });
            } else {
                enqueueSnackbar(error.response?.data?.error?.name, {
                    variant: "warning",
                });
            }
            // enqueueSnackbar(error.response?.data?.error?.name, { variant: 'warning' });
        }
    };

    const updateUser = async (values: any) => {
        try {
            setSaveLoader(true);
            const updatePayload = {
                firstName: values?.userAttributes?.firstName,
                lastName: values?.userAttributes?.lastName,
                role: values?.userAttributes?.role,
                viewPermissions: values?.userAttributes?.viewPermissions,
                editPermissions: values?.userAttributes?.editPermissions,
            };
            const response = await axios.put(
                `${process.env.BASE_URL}api/v1/cms/user/${values?.username}`,
                updatePayload
            );
            setSaveLoader(false);
            if (response.data?.success) {
                closeModal();
                enqueueSnackbar("User updated successfully.", {
                    variant: "success",
                });
                if (
                    userTableRef.current &&
                    userTableRef.current.refreshUserList
                ) {
                    userTableRef.current.refreshUserList();
                }
                resetForm();
            } else {
                enqueueSnackbar(response.data.message, { variant: "error" });
            }
        } catch (error: any) {
            setSaveLoader(false);
            enqueueSnackbar(error.response?.data?.error?.name, {
                variant: "warning",
            });
        }
    };

    const handlePermissionChange = (
        permissionType: "viewPermissions" | "editPermissions",
        moduleName: string
    ) => {
        const currentPermissions = values.userAttributes[permissionType];
        const updatedPermissions = currentPermissions.includes(moduleName)
            ? currentPermissions.filter((p: string) => p !== moduleName)
            : [...currentPermissions, moduleName];
        setFieldValue(`userAttributes.${permissionType}`, updatedPermissions);

        if (permissionType === "editPermissions") {
            const updatedViewPermissions = updatedPermissions.includes(
                moduleName
            )
                ? [
                      ...new Set([
                          ...values.userAttributes.viewPermissions,
                          moduleName,
                      ]),
                  ]
                : values.userAttributes.viewPermissions;
            setFieldValue(
                "userAttributes.viewPermissions",
                updatedViewPermissions
            );
        }

        // Clear permission error if at least one permission is selected
        if (
            updatedPermissions.length > 0 ||
            values.userAttributes[
                permissionType === "viewPermissions"
                    ? "editPermissions"
                    : "viewPermissions"
            ].length > 0
        ) {
            setPermissionError("");
        }
    };

    const onHandleEdit = (userInfo: any) => {
        setIsEdit(true);
        setShowCreateModal(true);
        setValues({
            username: userInfo.username,
            userAttributes: {
                firstName: userInfo?.firstName,
                lastName: userInfo?.lastName,
                email: userInfo?.email,
                role: userInfo?.role,
                viewPermissions: userInfo?.viewPermissions?.split(",") || [],
                editPermissions: userInfo?.editPermissions?.split(",") || [],
            },
        });
    };

    return (
        <div className="config-heading">
            <div className="header-content-list">
                <div>
                    <h3>User Management</h3>
                </div>
                <div className="d-flex j-center">
                    {hasRole(ROLE.ADMIN) && (
                        <Button
                            className="float-right mb-2"
                            onClick={() => setShowCreateModal(true)}
                            variant="contained"
                        >
                            Add User
                        </Button>
                    )}
                </div>
            </div>
            <UserTable
                ref={userTableRef}
                modules={modules}
                roles={roles}
                onEdit={onHandleEdit}
            />

            {showCreateModal && (
                <Dialog
                    maxWidth="md"
                    fullWidth
                    open={showCreateModal}
                    onClose={closeModal}
                >
                    <DialogTitle>
                        <span style={{ fontSize: "large", fontWeight: "bold" }}>
                            {isEdit ? "Edit" : "Add"} User
                        </span>
                    </DialogTitle>

                    <DialogContent>
                        <Grid container spacing={2} marginTop={1}>
                            {isEdit ? (
                                <>
                                    <Grid item xs={4}>
                                        <Typography
                                            variant="subtitle1"
                                            style={{ fontWeight: "bold" }}
                                        >
                                            First Name
                                        </Typography>
                                        <Typography variant="body1">
                                            {values?.userAttributes
                                                ?.firstName || "-"}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={4}>
                                        <Typography
                                            variant="subtitle1"
                                            style={{ fontWeight: "bold" }}
                                        >
                                            Last Name
                                        </Typography>
                                        <Typography variant="body1">
                                            {values?.userAttributes?.lastName ||
                                                "-"}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={4}>
                                        <Typography
                                            variant="subtitle1"
                                            style={{ fontWeight: "bold" }}
                                        >
                                            Email
                                        </Typography>
                                        <Typography variant="body1">
                                            {values?.userAttributes?.email ||
                                                "-"}
                                        </Typography>
                                    </Grid>
                                </>
                            ) : (
                                <>
                                    <Grid item xs={6}>
                                        <TextField
                                            fullWidth
                                            id="userAttributes.firstName"
                                            name="userAttributes.firstName"
                                            label="First Name"
                                            value={
                                                values.userAttributes.firstName
                                            }
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            error={
                                                touched.userAttributes
                                                    ?.firstName &&
                                                Boolean(
                                                    errors.userAttributes
                                                        ?.firstName
                                                )
                                            }
                                            helperText={
                                                touched.userAttributes
                                                    ?.firstName &&
                                                errors.userAttributes?.firstName
                                            }
                                        />
                                    </Grid>
                                    <Grid item xs={6}>
                                        <TextField
                                            fullWidth
                                            id="userAttributes.lastName"
                                            name="userAttributes.lastName"
                                            label="Last Name"
                                            value={
                                                values.userAttributes.lastName
                                            }
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            error={
                                                touched.userAttributes
                                                    ?.lastName &&
                                                Boolean(
                                                    errors.userAttributes
                                                        ?.lastName
                                                )
                                            }
                                            helperText={
                                                touched.userAttributes
                                                    ?.lastName &&
                                                errors.userAttributes?.lastName
                                            }
                                        />
                                    </Grid>
                                    <Grid item xs={8}>
                                        <TextField
                                            fullWidth
                                            id="userAttributes.email"
                                            name="userAttributes.email"
                                            label="Email"
                                            value={values.userAttributes.email}
                                            onChange={(e) => {
                                                handleChange(e);
                                                setFieldValue(
                                                    "username",
                                                    e.target.value
                                                );
                                            }}
                                            onBlur={handleBlur}
                                            error={
                                                touched.userAttributes?.email &&
                                                Boolean(
                                                    errors.userAttributes?.email
                                                )
                                            }
                                            helperText={
                                                touched.userAttributes?.email &&
                                                errors.userAttributes?.email
                                            }
                                        />
                                    </Grid>
                                </>
                            )}

                            <Grid item xs={4}>
                                <FormControl fullWidth>
                                    <InputLabel id="role-label">
                                        Role
                                    </InputLabel>
                                    <Select
                                        labelId="role-label"
                                        id="userAttributes.role"
                                        name="userAttributes.role"
                                        value={values.userAttributes.role}
                                        label="Role"
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        disabled={
                                            !(hasRole(ROLE.ADMIN) || isEdit)
                                        }
                                        error={
                                            touched.userAttributes?.role &&
                                            Boolean(errors.userAttributes?.role)
                                        }
                                    >
                                        {roles.map(
                                            (role: {
                                                roleLabel: string;
                                                roleName: string;
                                            }) => (
                                                <MenuItem
                                                    key={role.roleName}
                                                    value={role.roleName}
                                                >
                                                    {role.roleLabel}
                                                </MenuItem>
                                            )
                                        )}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12}>
                                <h4>View Modules</h4>
                                <Grid container spacing={2}>
                                    <PermissionModuleGrid
                                        modules={modules}
                                        permissionType="viewPermissions"
                                        values={values.userAttributes}
                                        onPermissionChange={handlePermissionChange}
                                    />
                                </Grid>
                            </Grid>

                            <Grid item xs={12}>
                                <h4>Edit Modules</h4>
                                <Grid container spacing={2}>
                                    <PermissionModuleGrid
                                        modules={modules}
                                        permissionType="editPermissions"
                                        values={values.userAttributes}
                                        onPermissionChange={handlePermissionChange}
                                    />
                                </Grid>
                            </Grid>
                        </Grid>
                    </DialogContent>
                    <DialogActions>
                        <Button
                            variant="contained"
                            color="warning"
                            onClick={closeModal}
                        >
                            Cancel
                        </Button>
                        <Button
                            startIcon={
                                saveLoader && (
                                    <CircularProgress
                                        style={{ color: "white" }}
                                        size={24}
                                    />
                                )
                            }
                            disabled={saveLoader}
                            variant="contained"
                            color="primary"
                            onClick={() => handleSubmit()}
                        >
                            Save
                        </Button>
                    </DialogActions>
                    {permissionError && (
                        <Typography
                            color="error"
                            align="center"
                            style={{ marginBottom: "16px" }}
                        >
                            {permissionError}
                        </Typography>
                    )}
                </Dialog>
            )}
        </div>
    );
};

export default UserManagement;
